/**
 * 친구 프로필 이미지 가져오기
 *
 * Cloud Function을 통해 Threads 프로필 페이지에서 프로필 이미지 스크래핑
 * → Firebase Storage에 저장 → Storage URL 반환 (CORS 문제 해결)
 * Firestore 캐싱으로 반복 요청 최소화 (TTL 7일)
 */

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { dbService as db } from '../firebaseInstance';

const FETCH_FRIEND_PROFILES_URL = "https://fetchfriendprofiles-rwo7wzkzaa-uc.a.run.app";
const CACHE_TTL_DAYS = 7;

/**
 * Firestore에서 캐시된 프로필 이미지 가져오기
 *
 * @param {string} myUserId - 현재 사용자 ID
 * @param {string} username - 친구 username
 * @returns {Promise<{storageUrl: string|null, friendUserId: string|null}|null>}
 */
const getCachedProfilePic = async (myUserId, username) => {
  try {
    const cacheRef = doc(db, 'user', myUserId, 'friendProfileCache', username);
    const cacheDoc = await getDoc(cacheRef);

    if (!cacheDoc.exists()) {
      return null;
    }

    const data = cacheDoc.data();
    const cachedAt = data.cachedAt?.toDate?.() || new Date(data.cachedAt);
    const now = new Date();
    const diffDays = (now - cachedAt) / (1000 * 60 * 60 * 24);

    // TTL 만료 체크
    if (diffDays > CACHE_TTL_DAYS) {
      console.log(`🔄 Cache expired for @${username} (${diffDays.toFixed(1)} days old)`);
      return null;
    }

    // storageUrl이 있으면 사용, 없으면 캐시 미스 처리
    if (data.storageUrl) {
      console.log(`✅ Cache hit for @${username} (Storage URL)`);
      return {
        storageUrl: data.storageUrl,
        friendUserId: data.friendUserId || null,
      };
    }

    // 이전 캐시 형식 (profilePicUrl만 있는 경우) - 캐시 미스로 처리하여 재요청
    console.log(`🔄 Cache has old format for @${username}, will refetch`);
    return null;
  } catch (error) {
    console.warn(`Cache read error for @${username}:`, error);
    return null;
  }
};

/**
 * Firestore에 프로필 이미지 캐싱
 *
 * @param {string} myUserId - 현재 사용자 ID
 * @param {string} username - 친구 username
 * @param {string} friendUserId - 친구 사용자 ID
 * @param {string|null} storageUrl - Firebase Storage URL
 */
const setCachedProfilePic = async (myUserId, username, friendUserId, storageUrl) => {
  try {
    const cacheRef = doc(db, 'user', myUserId, 'friendProfileCache', username);
    await setDoc(cacheRef, {
      username,
      friendUserId,
      storageUrl,
      cachedAt: Timestamp.now(),
    });
    console.log(`💾 Cached Storage URL for @${username}`);
  } catch (error) {
    console.warn(`Cache write error for @${username}:`, error);
  }
};

/**
 * 여러 사용자의 프로필 이미지 URL 가져오기 (캐시 우선, Storage URL 반환)
 *
 * @param {Array<{username: string, userId: string}>} friends - 친구 목록 (username, userId 포함)
 * @param {string} [myUserId] - 현재 사용자 ID (캐싱용)
 * @returns {Promise<Array<{username: string, oderId: string, storageUrl: string|null}>>}
 */
export const fetchFriendProfilePictures = async (friends, myUserId = null) => {
  if (!friends || friends.length === 0) {
    return [];
  }

  // myUserId가 없으면 localStorage에서 가져오기
  const effectiveUserId = myUserId || localStorage.getItem('userID');

  // 캐시에서 먼저 확인
  const results = [];
  const uncachedFriends = [];

  if (effectiveUserId) {
    console.log('🔍 Checking cache for', friends.length, 'friends... (parallel)');

    // 🚀 병렬 캐시 조회 (개별 실패 허용)
    const cachePromises = friends.map(friend =>
      getCachedProfilePic(effectiveUserId, friend.username)
        .then(cached => ({ friend, cached }))
        .catch(() => ({ friend, cached: null }))
    );
    const cacheResults = await Promise.all(cachePromises);

    cacheResults.forEach(({ friend, cached }) => {
      if (cached?.storageUrl) {
        results.push({
          username: friend.username,
          oderId: cached.friendUserId || friend.userId,
          storageUrl: cached.storageUrl,
        });
      } else {
        uncachedFriends.push(friend);
      }
    });

    console.log(`📊 Cache: ${results.length} hits, ${uncachedFriends.length} misses`);
  } else {
    // userId 없으면 모두 Cloud Function에서 가져오기
    uncachedFriends.push(...friends);
  }

  // 캐시되지 않은 것들만 Cloud Function에서 가져오기
  if (uncachedFriends.length > 0) {
    try {
      console.log('☁️ Fetching', uncachedFriends.length, 'profiles from Cloud Function (with Storage upload)...');

      // 새로운 형식: users 배열로 전송 (username, userId 포함)
      const response = await fetch(FETCH_FRIEND_PROFILES_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          users: uncachedFriends.map(f => ({
            username: f.username,
            userId: f.userId || f.user_id || f.id || null,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const fetchedResults = await response.json();
      console.log("📸 Fetched from Cloud Function:", fetchedResults.length, "profiles");

      // 결과 저장 및 캐싱
      for (const result of fetchedResults) {
        // storageUrl 우선, 없으면 null
        const storageUrl = result.storageUrl || null;

        results.push({
          username: result.username,
          oderId: result.userId,
          storageUrl,
        });

        // Firestore에 캐싱 (effectiveUserId가 있고, storageUrl이 있을 때)
        if (effectiveUserId && storageUrl) {
          setCachedProfilePic(effectiveUserId, result.username, result.userId, storageUrl);
        }
      }
    } catch (error) {
      console.error("Error fetching friend profile pictures:", error);
      // 실패한 것들은 null로 추가
      uncachedFriends.forEach(friend => {
        if (!results.find(r => r.username === friend.username)) {
          results.push({ username: friend.username, oderId: friend.userId, storageUrl: null });
        }
      });
    }
  }

  return results;
};

/**
 * friendsOfTheYear 배열에 프로필 이미지 병합 (Firebase Storage URL 사용)
 *
 * @param {Array} friendsOfTheYear - 친구 목록
 * @returns {Promise<Array>} Storage URL이 추가된 친구 목록
 */
export const enrichFriendsWithProfilePictures = async (friendsOfTheYear) => {
  if (!friendsOfTheYear || friendsOfTheYear.length === 0) {
    return friendsOfTheYear;
  }

  // 이미 프로필 이미지가 있는 친구는 제외 (Storage URL 형식인 경우)
  const friendsNeedingPictures = friendsOfTheYear.filter(f => {
    const hasStorageUrl = f.profileImage?.includes('firebasestorage.googleapis.com');
    return !hasStorageUrl;
  });

  if (friendsNeedingPictures.length === 0) {
    console.log('✅ All friends already have Storage URLs');
    return friendsOfTheYear;
  }

  // username과 userId를 함께 전달
  const friendsForFetch = friendsNeedingPictures.map(f => ({
    username: f.username,
    userId: f.userId || f.user_id || f.id || null,
  }));

  const profilePictures = await fetchFriendProfilePictures(friendsForFetch);

  // 결과를 맵으로 변환 (username → storageUrl)
  const pictureMap = {};
  profilePictures.forEach(p => {
    if (p.storageUrl) {
      pictureMap[p.username] = p.storageUrl;
    }
  });

  console.log(`📸 Enriched ${Object.keys(pictureMap).length}/${friendsOfTheYear.length} friends with Storage URLs`);

  // friendsOfTheYear에 프로필 이미지 병합
  return friendsOfTheYear.map(friend => ({
    ...friend,
    profileImage: friend.profileImage?.includes('firebasestorage.googleapis.com')
      ? friend.profileImage  // 이미 Storage URL이면 유지
      : (pictureMap[friend.username] || friend.profileImage || ""),
  }));
};
